<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <button
    :type="submit ? 'submit' : 'button'"
    class="amaiz-button"
    :class="[className, type, size, { 'w-full': fullWidth, danger, loading: isLoading }]"
    :disabled="disabled"
    :hidden="hidden"
    @click="clickHandler($event as PointerEvent)">
    <div
      class="flex items-center gap-4"
      :class="{ 'opacity-0': isLoading }">
      <slot>
        <SvgIcon
          v-if="leftIcon"
          id="leftIcon"
          type="sized"
          :name="leftIcon.name"
          :size="iconSize"/>
        <span>{{ label }}</span>
        <SvgIcon
          v-if="rightIcon"
          id="rightIcon"
          type="sized"
          :name="rightIcon.name"
          :size="iconSize"/>
      </slot>
    </div>
    <LoaderIcon
      v-if="isLoading"
      :size="iconSize"
      :color="loadingIconColor"
      class="absolute"/>
  </button>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { ButtonVariant } from '@/_redesign/shared/types/ButtonVariant';
import { OneSizedIcon } from '@/_redesign/shared/types/IconProps';
import LoaderIcon from '@/_redesign/shared/ui/LoaderIcon/LoaderIcon.vue';
import SvgIcon from '@/_redesign/shared/ui/SvgIcon/SvgIcon.vue';
import { VueClass } from '@/types/VueClass';

const {
  label = '',
  type = 'primary',
  size = 'large',
  class: className = '',
  submit = false,
  danger = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  forRouterLink,
  rightIcon,
  leftIcon,

  onClick,
} = defineProps<{
  label?: string;
  type?: ButtonVariant;
  size?: 'large' | 'medium' | 'small' | 'tiny';
  class?: VueClass;
  submit?: boolean;
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  hidden?: boolean;
  /** Required for SPA routing. Without it RouterLink will work as regualar link */
  forRouterLink?: boolean;
  leftIcon?: OneSizedIcon<'sized', 16>;
  rightIcon?: OneSizedIcon<'sized', 16>;

  onClick?: (payload: PointerEvent) => unknown | Promise<unknown>;
}>();

const innerLoading = ref(false);

const isLoading = computed(() => (loading || innerLoading.value) && !disabled);
const iconSize = computed(() => {
  if (size === 'tiny') return 12;
  return 16;
});

const loadingIconColor = computed(() => {
  if (type === 'primary') return 'default';
  return danger ? 'red' : 'blue';
});

async function clickHandler(pointerEvent: PointerEvent) {
  if (!forRouterLink) pointerEvent.stopPropagation();

  if (loading || disabled) return;
  innerLoading.value = true;
  await onClick?.(pointerEvent);
  innerLoading.value = false;
}
</script>

<style lang="scss" scoped>
.amaiz-button {
  @apply flex items-center justify-center transition-all duration-300 whitespace-nowrap;

  &.large {
    @apply w-min h-48 min-w-132 px-32 text-button-large rounded-12;
  }

  &.medium {
    @apply w-min h-40 min-w-102 px-24 text-button-medium rounded-12;
  }

  &.small {
    @apply w-min h-32 min-w-80 px-16 text-button-small rounded-8;
  }

  &.tiny {
    @apply w-min h-24 min-w-64 px-12 text-button-tiny rounded-8;
  }

  &.primary {
    @apply text-on-accent-primary-default;

    &:not(:disabled, .danger) {
      @apply bg-accent-primary-default hover:bg-accent-primary-hover active:bg-accent-primary-selected;

      &.loading {
        @apply bg-accent-primary-selected;
      }
    }

    &:not(:disabled).danger {
      @apply bg-negative-primary-default hover:bg-negative-primary-hover active:bg-negative-primary-selected;

      &.loading {
        @apply bg-negative-primary-selected;
      }
    }

    &:disabled {
      @apply text-neutral-primary-disabled bg-neutral-primary-disabled;
    }
  }

  &.secondary {
    &:not(:disabled, .danger) {
      @apply text-accent-primary-default bg-accent-secondary-default hover:bg-accent-secondary-hover active:bg-accent-secondary-selected;

      &.loading {
        @apply bg-accent-secondary-selected;
      }
    }

    &:not(:disabled).danger {
      @apply text-negative-default bg-negative-secondary-default hover:bg-negative-secondary-hover active:bg-negative-secondary-selected;

      &.loading {
        @apply bg-negative-secondary-selected;
      }
    }

    &:disabled {
      @apply text-neutral-secondary-disabled bg-neutral-secondary-disabled;
    }
  }
}
</style>
